/**
 * The AI models an analysis can run on, for the model picker.
 *
 * Today only an admin sees the picker, and only in Preview mode. The server
 * decides what actually runs (supabase/functions/_shared/aiTiers.ts): it
 * honours a chosen set for admins and ignores it for everyone else, so this
 * file is a menu, never a permission.
 *
 * Keep in step with SELECTABLE_MODELS in supabase/functions/_shared/aiTiers.ts
 * and worker/src/agent/tiers.ts.
 */

export type AnalysisAiKind = "cro" | "content" | "technical" | "heatmap" | "competitor" | "agent";

export interface SelectableModel {
  id: string;
  label: string;
  /** Strength order from the A/B (September 2026): the highest chosen writes and merges. */
  rank: number;
  note: string;
}

export const SELECTABLE_MODELS: readonly SelectableModel[] = [
  { id: "google/gemini-3.7-flash", label: "Gemini 3.7 Flash", rank: 1, note: "Fast and cheap. Admin tests only: customer audits never run on it." },
  { id: "x-ai/grok-4.7", label: "Grok 4.7", rank: 2, note: "Good second writer next to Sonnet." },
  { id: "anthropic/claude-sonnet-5.5", label: "Claude Sonnet 5.5", rank: 3, note: "Default for CRO, Competitor and the User Agent, Free included. Stands in when Sol fails." },
  { id: "openai/gpt-6.1-sol", label: "GPT-6.1 Sol", rank: 4, note: "Default for Content, Technical, Heatmap and Quick Check, Free included. Stands in when Sonnet fails." },
  { id: "anthropic/claude-opus-5.5", label: "Claude Opus 5.5", rank: 5, note: "Strongest and slowest. Best as the merger." },
];

/** How many chosen models write a draft. The merge takes two drafts today. */
export const MAX_WRITERS = 2;

/** Analyses where a second model writes a parallel draft that the strongest merges. */
export const MULTI_WRITER_KINDS: ReadonlySet<AnalysisAiKind> = new Set<AnalysisAiKind>(["cro", "content"]);

/** The default per analysis (every plan, Free included), shown when nothing is chosen. */
export const DEFAULT_MODEL_BY_KIND: Readonly<Record<AnalysisAiKind, string>> = {
  cro: "anthropic/claude-sonnet-5.5",
  content: "openai/gpt-6.1-sol",
  technical: "openai/gpt-6.1-sol",
  heatmap: "openai/gpt-6.1-sol",
  competitor: "anthropic/claude-sonnet-5.5",
  agent: "anthropic/claude-sonnet-5.5",
};

const rankOf = (id: string) => SELECTABLE_MODELS.find((m) => m.id === id)?.rank ?? 0;

export const modelLabel = (id: string) => SELECTABLE_MODELS.find((m) => m.id === id)?.label ?? id;

/** Known ids only, no repeats, strongest first, at most MAX_WRITERS. */
export function orderModels(ids: readonly string[]): string[] {
  return ids
    .map((x) => String(x ?? "").trim().toLowerCase())
    .filter((x, i, a) => rankOf(x) > 0 && a.indexOf(x) === i)
    .sort((x, y) => rankOf(y) - rankOf(x))
    .slice(0, MAX_WRITERS);
}

/** The plan string the start functions accept ("models:a+b"), or undefined for the plan default. */
export function modelsTier(ids: readonly string[] | null | undefined): string | undefined {
  const list = orderModels(ids ?? []);
  return list.length ? `models:${list.join("+")}` : undefined;
}

/** Who does what with this choice on this analysis, in one line. */
export function describeChoice(ids: readonly string[], kind: AnalysisAiKind): string {
  const list = orderModels(ids);
  if (!list.length) return `Plan default: ${modelLabel(DEFAULT_MODEL_BY_KIND[kind])}.`;
  if (list.length === 1) return `${modelLabel(list[0])} writes the report.`;
  if (MULTI_WRITER_KINDS.has(kind)) {
    return `${modelLabel(list[0])} and ${modelLabel(list[1])} write in parallel; ${modelLabel(list[0])} keeps what the evidence supports and merges.`;
  }
  return `${modelLabel(list[0])} writes the report. Two writers run on CRO and Content only for now.`;
}
